import {
    Box,
    FormControl,
    FormLabel,
    Stack,
    Text,
    VStack,
    Link,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Field } from 'formik';
import { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ExternalLinkIcon } from '@chakra-ui/icons';

import FieldError from 'components/forms/new/FieldError';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    descriptionStyling,
    dropDownFormStyle,
    formSelectStyles,
    formLabelStyling,
    stackSpacing,
    stackStyling,
    textBoxStyling,
} from 'components/forms/new/formStyles';

const RegistrarRegionField = ({
    errors,
    values,
    touched,
    regions = [],
    darkMode,
    mode = 'Add',
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const isAddPage = useMemo(() => mode === 'Add', [mode]);

    const selectedRegion = useMemo(() => {
        if (!regions.length || !values?.regionID) return null;
        return regions.find((r) => r.id === values.regionID) || null;
    }, [regions, values?.regionID]);

    const baseStyles = dropDownFormStyle(darkMode);
    const linkToIpRanges = '/Guides/pbx_ip_ranges_configuration.pdf';

    return (
        <FormControl
            isRequired={isAddPage}
            sx={{ margin: '0 !important' }}
            isInvalid={!!errors.regionID && touched.regionID}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Region
                    </FormLabel>
                    <Text
                        sx={{
                            ...descriptionStyling,
                            color: darkMode ? 'gray.100' : '#757575',
                        }}
                        maxW={250}>
                        Choose from available regions
                    </Text>
                    <Link
                        as={RouterLink}
                        to={linkToIpRanges}
                        isExternal
                        state={{ regionID: selectedRegion?.id ?? '' }}
                        color={darkMode ? 'brand.400' : 'brand.500'}
                        _hover={{
                            color: darkMode ? 'brand.500' : 'brand.600',
                        }}
                        fontSize="sm"
                        display="flex"
                        alignItems="center"
                        gap={1}>
                        IP ranges for firewall configuration
                        <ExternalLinkIcon w={3} h={3} />
                    </Link>
                </Box>
                <Field name="regionID">
                    {({ field, form: { setFieldValue, setTouched } }) => (
                        <Box
                            flex="1"
                            sx={{
                                paddingTop: '15px',
                                ...formSelectStyles,
                            }}>
                            <VStack width="100%" alignItems="normal">
                                <Select
                                    {...field}
                                    tabSelectsValue
                                    openMenuOnFocus
                                    placeholder="Select region"
                                    sx={{
                                        ...textBoxStyling,
                                        color: darkMode
                                            ? 'dark.textDark'
                                            : null,
                                        background: darkMode
                                            ? 'dark.bgDark'
                                            : null,
                                        borderColor: darkMode
                                            ? 'dark.tcap-borderGray'
                                            : null,
                                    }}
                                    value={selectedRegion}
                                    options={regions}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    chakraStyles={baseStyles}
                                    onChange={(selectedOption) => {
                                        const regionId =
                                            selectedOption?.id || '';
                                        setFieldValue('regionID', regionId);
                                    }}
                                    onBlur={() => {
                                        setTouched({
                                            ...touched,
                                            regionID: true,
                                        });
                                    }}
                                    selectedOptionColor="brand"
                                />
                            </VStack>
                        </Box>
                    )}
                </Field>

                {isHovered && (
                    <FieldToolTip
                        label="Select the geographical region closest to your SIP endpoint or PBX. Choose the nearest region minimizes network latency and can improve call quality."
                        ariaLabel="registrar-region-tip"
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
            {isAddPage && (
                <FieldError
                    error={errors?.regionID}
                    touched={touched?.regionID}
                />
            )}
        </FormControl>
    );
};

export default RegistrarRegionField;
